import { Popover, Tooltip } from "antd";
import { Badge } from "reactstrap";
import dayjs from "dayjs";

// WBS upload preview columns, moved verbatim from
// src/pages/pre-built/user-manage/ExcelUploadRequestedList.tsx
// drawerColConfig["wbs-upload"] so the Uploads History drawer and the WBS
// upload modal render the exact same preview.
export const wbsUploadPreviewColumns: any[] = [
  {
    title: "Status",
    key: "status",
    width: 140,
    fixed: "left",
    render: (text: any, record: any) => {
      const hasArrayErrors =
        Array.isArray(record?.Error) && record.Error.length > 0;
      const hasStringError =
        typeof record?.Error === "string" && record.Error.trim().length > 0;
      const hasErrors = hasArrayErrors || hasStringError;

      const errors = hasArrayErrors
        ? record.Error
        : hasStringError
        ? [record.Error]
        : [];

      if (hasErrors) {
        return (
          <Popover
            placement="right"
            content={
              <div style={{ maxWidth: 300 }}>
                {errors.map((error: string, index: number) => (
                  <div key={index} style={{ marginBottom: 4 }}>
                    • {error}
                  </div>
                ))}
              </div>
            }
            title={
              <span style={{ color: "red", fontWeight: "600" }}>
                Validation Errors
              </span>
            }
          >
            <Badge color="red" className="upload-badge failed">
              Failed
            </Badge>
          </Popover>
        );
      } else {
        return (
          <Popover
            content={
              <div>
                <p>This record is valid and ready for import</p>
              </div>
            }
            placement="right"
            title={<span className="upload-badge success">Success</span>}
          >
            <Badge color="green" className="upload-badge success">
              Success
            </Badge>
          </Popover>
        );
      }
    },
  },
  // Phase Section
  {
    title: "Phase Name",
    dataIndex: "phaseName",
    key: "phaseName",
    width: 150,
  },
  {
    title: "Phase Owner",
    dataIndex: "phaseOwner",
    key: "phaseOwner",
    width: 130,
  },
  {
    title: "Phase Start Date",
    dataIndex: "phaseStartDate",
    key: "phaseStartDate",
    width: 120,
    render: (text: any) => (text ? dayjs(text).format("DD-MM-YYYY") : "-"),
  },
  {
    title: "Phase End Date",
    dataIndex: "phaseEndDate",
    key: "phaseEndDate",
    width: 120,
    render: (text: any) => (text ? dayjs(text).format("DD-MM-YYYY") : "-"),
  },
  {
    title: "Phase Weightage(%)",
    dataIndex: "phaseWeightage",
    key: "phaseWeightage",
    width: 150,
  },
  // Activity Section
  {
    title: "Activity Name",
    dataIndex: "activityName",
    key: "activityName",
    width: 150,
  },
  {
    title: "Activity Owner",
    dataIndex: "activityOwner",
    key: "activityOwner",
    width: 130,
  },
  {
    title: "Activity Start Date",
    dataIndex: "activityStartDate",
    key: "activityStartDate",
    width: 150,
    render: (text: any) => (text ? dayjs(text).format("DD-MM-YYYY") : "-"),
  },
  {
    title: "Activity End Date",
    dataIndex: "activityEndDate",
    key: "activityEndDate",
    width: 120,
    render: (text: any) => (text ? dayjs(text).format("DD-MM-YYYY") : "-"),
  },
  {
    title: "Activity Weightage(%)",
    dataIndex: "activityWeightage",
    key: "activityWeightage",
    width: 150,
  },
  // Task Section
  {
    title: "Task ID",
    dataIndex: "taskId",
    key: "taskId",
    width: 100,
  },
  {
    title: "Task Name",
    dataIndex: "taskName",
    key: "taskName",
    width: 150,
  },
  {
    title: "Internal Task Number",
    dataIndex: "internalTaskNumber",
    key: "internalTaskNumber",
    width: 160,
  },
  {
    title: "Task Category",
    dataIndex: "taskCategory",
    key: "taskCategory",
    width: 120,
  },
  {
    title: "Task Owner",
    dataIndex: "taskOwner",
    key: "taskOwner",
    width: 130,
  },
  {
    title: "Task Start Date",
    dataIndex: "taskStartDate",
    key: "taskStartDate",
    width: 120,
    render: (text: any) => (text ? dayjs(text).format("DD-MM-YYYY") : "-"),
  },
  {
    title: "Task End Date",
    dataIndex: "taskEndDate",
    key: "taskEndDate",
    width: 120,
    render: (text: any) => (text ? dayjs(text).format("DD-MM-YYYY") : "-"),
  },
  {
    title: "Task Weightage(%)",
    dataIndex: "taskWeightage",
    key: "taskWeightage",
    width: 150,
  },
  {
    title: "Contractor Name",
    dataIndex: "contractorName",
    key: "contractorName",
    width: 160,
  },
  // Task Details
  {
    title: "Work Type",
    dataIndex: "workType",
    key: "workType",
    width: 100,
  },
  {
    title: "Work Quantity",
    dataIndex: "workQuantity",
    key: "workQuantity",
    width: 100,
  },
  {
    title: "Work UOM",
    dataIndex: "workUom",
    key: "workUom",
    width: 100,
  },
  {
    // The GST-inclusive contract rate. Base / GST / labour cess / Basic Rate are
    // derived server-side from this and the two percentage columns below.
    title: "Work Rate (incl. GST)",
    dataIndex: "rate",
    key: "rate",
    width: 120,
  },
  {
    // Optional. Blank falls back to the 18 / 1 defaults.
    title: "GST %",
    dataIndex: "gstPercent",
    key: "gstPercent",
    width: 80,
  },
  {
    title: "Cess %",
    dataIndex: "cessPercent",
    key: "cessPercent",
    width: 80,
  },
  {
    title: "Task Description",
    dataIndex: "taskDescription",
    key: "taskDescription",
    width: 200,
    render: (text: any) =>
      text ? (
        <Tooltip title={text}>
          <span
            style={{
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {text.length > 50 ? `${text.substring(0, 50)}...` : text}
          </span>
        </Tooltip>
      ) : (
        "-"
      ),
  },
  {
    title: "Priority",
    dataIndex: "priority",
    key: "priority",
    width: 100,
  },
  {
    title: "SiteStructureUnitId",
    dataIndex: "siteStructureUnitId",
    key: "siteStructureUnitId",
    width: 150,
  },
  {
    title: "Tower/Villa",
    dataIndex: "siteStructureUnitName",
    key: "siteStructureUnitName",
    width: 140,
  },
  {
    title: "SiteStructureSubUnitId",
    dataIndex: "siteStructureSubUnitId",
    key: "siteStructureSubUnitId",
    width: 170,
  },
  {
    title: "Flat/Shop",
    dataIndex: "siteStructureSubUnitName",
    key: "siteStructureSubUnitName",
    width: 130,
  },
  {
    title: "Floor",
    dataIndex: "floorNo",
    key: "floorNo",
    width: 100,
  },
  {
    title: "LocationId",
    dataIndex: "locationId",
    key: "locationId",
    width: 120,
  },
  {
    title: "LocationName",
    dataIndex: "locationName",
    key: "locationName",
    width: 150,
  },
  {
    title: "Dependency Type",
    dataIndex: "dependencyType",
    key: "dependencyType",
    width: 130,
  },
  {
    title: "Predecessor",
    dataIndex: "predecessor",
    key: "predecessor",
    width: 100,
  },
  {
    title: "Successor",
    dataIndex: "successor",
    key: "successor",
    width: 100,
  },
  {
    title: "Lag Days",
    dataIndex: "lagDays",
    key: "lagDays",
    width: 100,
  },
];

export const WBS_UPLOAD_TABLE_WIDTH = 4080;
